import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {Button, Card, Col, message, Row, Select, Space, Statistic, Typography} from "antd";
import {DownloadOutlined, FilePdfOutlined, FileTextOutlined, SafetyCertificateOutlined} from "@ant-design/icons";
import {LocalName} from "../../../../../../utils/LocalName";
import MicroqualificationGroupsService from "../../../../../../services/MicroqualificationGroupsService";
import {getGroupStudents} from "../../../../../../store/slices/MicroqualificationGroupsSlice";
import IntlMessage from "../../../../../../components/IntlMessage/IntlMessage";

const {Text, Title} = Typography;

const DocumentsTab = ({groupId, group}) => {
    const dispatch = useDispatch();
    const [selectedDisciplineForSheet, setSelectedDisciplineForSheet] = useState(null);
    const [selectedStudentForTranscript, setSelectedStudentForTranscript] = useState(null);
    const [loading, setLoading] = useState({
        assessmentSheet: false,
        transcript: false,
        certificates: false
    });

    const {students} = useSelector(state => state.microqualificationGroups);

    useEffect(() => {
        if (groupId) {
            dispatch(getGroupStudents(groupId));
        }
    }, [groupId, dispatch]);

    function triggerDownload(blob, filename) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    const handleDownloadAssessmentSheet = async () => {
        if (!selectedDisciplineForSheet) {
            message.warning(<IntlMessage id="mqGroups.selectDisciplinePlaceholder"/>);
            return;
        }
        message.loading(<IntlMessage id={'loading'}/>, 0);
        await MicroqualificationGroupsService.downloadAssessmentSheet(groupId, selectedDisciplineForSheet).then((r) => {
            message.destroy();
            message.success(<IntlMessage id={'success'}/>, 5);

            triggerDownload(r.data, "assessment_sheet.pdf")
        })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            })
    };

    const handleDownloadTranscript = async () => {
        if (!selectedStudentForTranscript) {
            message.warning(<IntlMessage id="mqGroups.selectStudent"/>);
            return;
        }

        setLoading(prev => ({...prev, transcript: true}));
        try {
            const response = await MicroqualificationGroupsService.downloadTranscript(
                groupId,
                selectedStudentForTranscript
            );
            const student = students.find(s => s.id === selectedStudentForTranscript);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `transcript_${student?.fullNameEng}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            message.success(<IntlMessage id="mqGroups.transcriptDownloaded"/>);
        } catch (error) {
            message.error(<IntlMessage id="mqGroups.failedToDownloadTranscript"/>);
        } finally {
            setLoading(prev => ({...prev, transcript: false}));
        }
    };

    const handleDownloadAllAssessmentSheets = async () => {
        if (!group?.disciplines || group.disciplines.length === 0) {
            message.warning(<IntlMessage id="mqGroups.noDisciplines"/>);
            return;
        }

        message.info(<IntlMessage id="mqGroups.downloadingAll" values={{type: 'assessment sheets'}}/>);
        for (const discipline of group.disciplines) {
            try {
                const response = await MicroqualificationGroupsService.downloadAssessmentSheet(
                    groupId,
                    discipline.id
                );
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `assessment_sheet_${group.name}_${discipline.code}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                await new Promise(resolve => setTimeout(resolve, 500));
            } catch (error) {
                console.error(`Failed to download sheet for ${discipline.code}`, error);
            }
        }
        message.success(<IntlMessage id="mqGroups.allDownloaded" values={{type: 'assessment sheets'}}/>);
    };

    const handleDownloadAllTranscripts = async () => {
        if (!students || students.length === 0) {
            message.warning(<IntlMessage id="noData"/>);
            return;
        }

        message.info(<IntlMessage id="mqGroups.downloadingAll" values={{type: 'transcripts'}}/>);
        for (const student of students) {
            try {
                const response = await MicroqualificationGroupsService.downloadTranscript(
                    groupId,
                    student.id
                );
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `transcript_${student.lastNameEng}_${student.firstNameEng}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                await new Promise(resolve => setTimeout(resolve, 500));
            } catch (error) {
                console.error(`Failed to download transcript for ${student.firstNameEng}`, error);
            }
        }
        message.success(<IntlMessage id="mqGroups.allDownloaded" values={{type: 'transcripts'}}/>);
    };

    const handleGenerateCertificates = async () => {
        if (!students || students.length === 0) {
            message.warning(<IntlMessage id="noData"/>);
            return;
        }

        setLoading(prev => ({...prev, certificates: true}));
        try {
            await MicroqualificationGroupsService.generateCertificates(groupId);
            message.success(<IntlMessage id="mqGroups.certificatesGenerated"/>);
        } catch (error) {
            message.error(<IntlMessage id="mqGroups.failedToGenerateCertificates"/>);
        } finally {
            setLoading(prev => ({...prev, certificates: false}));
        }
    };

    return (
        <Space direction="vertical" size="large" style={{width: '100%'}}>
            <Row gutter={16}>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title={<IntlMessage id="mqGroups.totalDisciplines"/>}
                            value={group?.disciplines?.length || 0}
                            prefix={<FileTextOutlined/>}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title={<IntlMessage id="mqGroups.totalStudents"/>}
                            value={students?.length || 0}
                            prefix={<FileTextOutlined/>}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title={<IntlMessage id="mqGroups.totalCredits"/>}
                            value={group?.credits || 0}
                            prefix={<FileTextOutlined/>}
                        />
                    </Card>
                </Col>
            </Row>

            <Card title={
                <Space>
                    <FilePdfOutlined/>
                    <Text strong><IntlMessage id="mqGroups.assessmentSheets"/></Text>
                </Space>
            }>
                <Space direction="vertical" size="middle" style={{width: '100%'}}>
                    <Text type="secondary">
                        <IntlMessage id="mqGroups.assessmentSheetDescription"/>
                    </Text>

                    <Space style={{width: '100%'}}>
                        <Select
                            style={{width: 400}}
                            placeholder={<IntlMessage id="mqGroups.selectDisciplinePlaceholder"/>}
                            value={selectedDisciplineForSheet}
                            onChange={setSelectedDisciplineForSheet}
                        >
                            {group?.disciplines?.map(discipline => (
                                <Select.Option key={discipline.id} value={discipline.id}>
                                    {discipline.code} - {LocalName.getName(discipline)}
                                </Select.Option>
                            ))}
                        </Select>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined/>}
                            onClick={handleDownloadAssessmentSheet}
                            loading={loading.assessmentSheet}
                        >
                            <IntlMessage id="mqGroups.downloadSelected"/>
                        </Button>
                        <Button
                            icon={<DownloadOutlined/>}
                            onClick={handleDownloadAllAssessmentSheets}
                        >
                            <IntlMessage id="mqGroups.downloadAll"/>
                        </Button>
                    </Space>
                </Space>
            </Card>

            <Card title={
                <Space>
                    <FilePdfOutlined/>
                    <Text strong><IntlMessage id="mqGroups.transcripts"/></Text>
                </Space>
            }>
                <Space direction="vertical" size="middle" style={{width: '100%'}}>
                    <Text type="secondary">
                        <IntlMessage id="mqGroups.transcriptDescription"/>
                    </Text>

                    <Space style={{width: '100%'}}>
                        <Select
                            style={{width: 400}}
                            placeholder={<IntlMessage id="mqGroups.selectStudent"/>}
                            value={selectedStudentForTranscript}
                            onChange={setSelectedStudentForTranscript}
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label?.toLowerCase() ?? '').includes(input.toLowerCase())
                            }
                        >
                            {students?.map(student => (
                                <Select.Option
                                    key={student.id}
                                    value={student.id}
                                    label={`${student.fullNameEng}`}
                                >
                                    {student.fullNameEng}
                                </Select.Option>
                            ))}
                        </Select>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined/>}
                            onClick={handleDownloadTranscript}
                            loading={loading.transcript}
                        >
                            <IntlMessage id="mqGroups.downloadSelected"/>
                        </Button>
                        <Button
                            icon={<DownloadOutlined/>}
                            onClick={handleDownloadAllTranscripts}
                        >
                            <IntlMessage id="mqGroups.downloadAll"/>
                        </Button>
                    </Space>
                </Space>
            </Card>

            <Card title={
                <Space>
                    <SafetyCertificateOutlined/>
                    <Text strong><IntlMessage id="mqGroups.certificates"/></Text>
                </Space>
            }>
                <Space direction="vertical" size="middle" style={{width: '100%'}}>
                    <Text type="secondary">
                        <IntlMessage id="mqGroups.certificatesDescription"/>
                    </Text>

                    <Button
                        type="primary"
                        size="large"
                        icon={<SafetyCertificateOutlined/>}
                        onClick={handleGenerateCertificates}
                        loading={loading.certificates}
                    >
                        <IntlMessage id="mqGroups.generateCertificates"/>
                    </Button>
                </Space>
            </Card>
        </Space>
    );
};

export default DocumentsTab;
